import { useState, useRef, useCallback } from "react";
import { Send, RotateCcw, AlertCircle } from "lucide-react";

export default function ManualAnalysis({ onAnalyze, loading, error }) {
  const [text, setText] = useState("");
  const [source, setSource] = useState("");
  const textareaRef = useRef(null);

  const canAnalyze = text.trim().length >= 5 && !loading;

  const handleAnalyze = useCallback(async () => {
    if (!canAnalyze) return;
    try {
      await onAnalyze(text.trim(), source.trim() || "@manual");
      setText("");
      // Keep source handle for repeated analyses
    } catch (err) {
      // Error handled by parent
    }
  }, [canAnalyze, onAnalyze, text, source]);

  const handleKeyDown = useCallback(
    (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        handleAnalyze();
      }
    },
    [handleAnalyze]
  );

  const handleClear = () => {
    setText("");
    setSource("");
    textareaRef.current?.focus();
  };

  return (
    <div
      className="rounded-[10px] border overflow-hidden"
      style={{ backgroundColor: '#161b22', borderColor: '#30363d' }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: '#30363d' }}
      >
        <span
          className="font-mono text-xs tracking-[0.12em] uppercase"
          style={{ color: '#8b949e' }}
        >
          Manual Analysis
        </span>
        <span
          className="font-mono text-[10px] tracking-[0.08em] uppercase px-2 py-0.5 rounded-full"
          style={{
            backgroundColor: loading ? '#1c2333' : '#0a2a1a',
            color: loading ? '#f59e0b' : '#22c55e',
            border: `1px solid ${loading ? '#30363d' : '#14532d'}`,
          }}
        >
          {loading ? 'ANALYZING...' : 'READY'}
        </span>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Source Handle */}
        <div>
          <label
            className="font-mono text-[11px] tracking-[0.08em] uppercase block mb-1.5"
            style={{ color: '#8b949e' }}
          >
            Source
          </label>
          <input
            data-testid="manual-analysis-source-input"
            type="text"
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="@zerohedge"
            className="w-full px-3 py-2 rounded-[6px] text-sm font-mono outline-none transition-colors duration-150"
            style={{
              backgroundColor: '#1c2333',
              borderColor: '#30363d',
              border: '1px solid #30363d',
              color: '#c9d1d9',
            }}
            onFocus={(e) => (e.target.style.borderColor = '#484f58')}
            onBlur={(e) => (e.target.style.borderColor = '#30363d')}
          />
        </div>

        {/* Headline Textarea */}
        <div>
          <label
            className="font-mono text-[11px] tracking-[0.08em] uppercase block mb-1.5"
            style={{ color: '#8b949e' }}
          >
            Headline / Post
          </label>
          <textarea
            ref={textareaRef}
            data-testid="manual-analysis-headline-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Paste a macro headline or news post here..."
            className="w-full px-3 py-2 rounded-[6px] text-sm outline-none resize-none transition-colors duration-150"
            style={{
              backgroundColor: '#1c2333',
              border: '1px solid #30363d',
              color: '#c9d1d9',
              minHeight: '140px',
              fontFamily: 'Inter, sans-serif',
            }}
            onFocus={(e) => (e.target.style.borderColor = '#484f58')}
            onBlur={(e) => (e.target.style.borderColor = '#30363d')}
          />
        </div>

        {/* Error */}
        {error && (
          <div
            className="flex items-start gap-2 px-3 py-2 rounded-[6px] text-sm"
            style={{
              backgroundColor: '#2a1418',
              border: '1px solid #7f1d1d',
              color: '#fca5a5',
            }}
          >
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <span className="font-sans text-xs">{error}</span>
          </div>
        )}

        {/* Buttons */}
        <div className="flex gap-2">
          <button
            data-testid="manual-analysis-analyze-button"
            onClick={handleAnalyze}
            disabled={!canAnalyze}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-[6px] font-mono text-xs tracking-[0.12em] uppercase font-semibold transition-colors duration-150"
            style={{
              backgroundColor: canAnalyze ? '#22c55e' : '#1c2333',
              color: canAnalyze ? '#07110b' : '#484f58',
              cursor: canAnalyze ? 'pointer' : 'not-allowed',
              opacity: loading ? 0.8 : 1,
            }}
          >
            {loading ? (
              <>
                <span
                  className="cw-spin inline-block rounded-full border-2"
                  data-testid="manual-analysis-loading-indicator"
                  style={{
                    width: 14,
                    height: 14,
                    borderColor: '#07110b',
                    borderTopColor: 'transparent',
                  }}
                />
                Analyzing...
              </>
            ) : (
              <>
                <Send size={13} />
                Analyze
              </>
            )}
          </button>
          <button
            data-testid="manual-analysis-clear-button"
            onClick={handleClear}
            className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-[6px] font-mono text-xs tracking-[0.12em] uppercase transition-colors duration-150"
            style={{
              backgroundColor: 'transparent',
              border: '1px solid #30363d',
              color: '#c9d1d9',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = '#1c2333')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
          >
            <RotateCcw size={12} />
            Clear
          </button>
        </div>

        {/* Keyboard hint */}
        <p
          className="font-mono text-[10px] text-center"
          style={{ color: '#484f58' }}
        >
          ⌘/Ctrl + Enter to analyze
        </p>
      </div>
    </div>
  );
}
